// The "person" model: one classless citizen whose capabilities come from
// attributes, learned skills (that rise with use and rust when idle), an age,
// and a work/soldier stance. Pure helpers shared by server systems (which mutate
// skills and derive combat/economy stats) and the client (which displays them).
//
// Phase 1: gear is implicit — a soldier fights with whichever weapon skill they
// are best at; a worker's output scales with the relevant economy skill.

import {
  GUARD_RANGE, DAY_TICKS, FOOD_CARRY, FOOD_METER_MAX, FOOD_WEIGHT, ITEMS, UNITS } from './constants'
import type { AttrKey, ItemType, Person, SkillKey, Unit } from './types'

export const SKILL_MAX = 10
export const ATTR_MAX = 10

export const SKILL_KEYS: SkillKey[] = [
  'build', 'farm', 'log', 'mine', 'haul',
  'fish', 'craft', 'unarmed', 'spear', 'sword', 'bow',
]
export const ATTR_KEYS: AttrKey[] = ['str', 'spd', 'int', 'cha', 'vit']

/** Economy skills — the ones a working citizen practises on the job. */
export const ECON_SKILLS: SkillKey[] = ['build', 'farm', 'log', 'mine', 'haul', 'craft']
/** Weapon skills a soldier can fight with. */
export type WeaponKey = 'unarmed' | 'spear' | 'sword' | 'bow'
export const WEAPON_KEYS: WeaponKey[] = ['unarmed', 'spear', 'sword', 'bow']

export const SKILL_LABEL: Record<SkillKey, string> = {
  build: 'Building', farm: 'Farming', log: 'Logging', mine: 'Mining', haul: 'Hauling',
  fish: 'Fishing', craft: 'Crafting', unarmed: 'Unarmed', spear: 'Spear', sword: 'Sword', bow: 'Bow',
}
export const ATTR_LABEL: Record<AttrKey, string> = {
  str: 'Strength', spd: 'Speed', int: 'Intelligence', cha: 'Charisma', vit: 'Vitality',
}
export const ATTR_ABBR: Record<AttrKey, string> = {
  str: 'STR', spd: 'SPD', int: 'INT', cha: 'CHA', vit: 'VIT',
}

// ---- Progression tuning (straw-man; tune later) --------------------------------

/** Skill gained per tick of actually doing the work/fighting. */
export const SKILL_XP_PER_TICK = 0.02
/** A skill unused for this many ticks (~2 days) begins to rust. */
export const RUST_GRACE = DAY_TICKS * 2
/** Skill lost per tick once rusting. */
export const RUST_PER_TICK = 0.0015
/** Rust never drops a skill below this fraction of its all-time peak. */
export const RUST_FLOOR_FRAC = 1 / 3
/** Ticks per displayed year of age (immortal for now; purely cosmetic). */
export const TICKS_PER_YEAR = DAY_TICKS * 3

/** A skill's effect multiplier — level 0 = 1×, level 10 ≈ 2×. */
export function skillMult(level: number): number {
  return 1 + 0.1 * level
}

/** Farming levels 0–5 yield 20%, 40%, 55%, 70%, 85%, 100%, then
 *  gain 5 percentage points per level above 5 (125% at the skill cap).
 *  Fractional skill progress contributes smoothly between whole levels. */
export function farmSkillMult(level: number): number {
  const skill = Math.max(0, Math.min(SKILL_MAX, Number.isFinite(level) ? level : 0))
  if (skill <= 1) return 0.2 + skill * 0.2
  return skill <= 5 ? 0.4 + (skill - 1) * 0.15 : 1 + (skill - 5) * 0.05
}

// ---- Implicit weapons (Phase 1) ------------------------------------------------

/** Base combat of each weapon a soldier can wield bare of any item. */
export const WEAPON_COMBAT: Record<WeaponKey, { atk: number; range: number; period: number }> = {
  unarmed: { atk: 2, range: 0, period: 1 }, // fists: same-hex brawling
  spear: { atk: 6, range: 1, period: 1 }, // reach: strikes the adjacent hex
  sword: { atk: 8, range: 0, period: 1 }, // close quarters: share the hex
  bow: { atk: 5, range: 3, period: 1 },
}

/** The weapon a person is actually holding, if it's a weapon (else null → fists). */
export function heldWeapon(p: Person): { key: WeaponKey; atk: number; range: number } | null {
  if (!p.held) return null
  const def = ITEMS[p.held]
  if (def.kind !== 'weapon') return null
  return { key: def.skill as WeaponKey, atk: def.atk ?? 2, range: def.range ?? 1 }
}

/** Is this person holding a weapon (as opposed to a tool, or nothing)? */
export function isArmed(p: Person): boolean {
  return !!p.held && ITEMS[p.held].kind === 'weapon'
}

/** The tool a building's job needs in hand — hammer for any construction site. */
export function requiredTool(buildingType: string): ItemType {
  switch (buildingType) {
    case 'farm': return 'hoe'
    case 'chop': return 'axe'
    case 'quarry': return 'pick'
    case 'mine': return 'pick'
    case 'barracks': return 'smithhammer' // the Smithy's anvil
    case 'pier': return 'rod'
    default: return 'hammer' // construction — and the Workbench hand-crafts fine with one
  }
}

/** Can this person work the given building, i.e. do they hold the right tool? */
export function canWork(p: Person, buildingType: string): boolean {
  if (buildingType === 'plant') return !isArmed(p)
  return !!p.held && p.held === requiredTool(buildingType) && ITEMS[p.held].kind === 'tool'
}

// ---- Derived stats -------------------------------------------------------------

/** Max health from Vitality. */
export function personMaxHp(p: Person): number {
  return Math.round(25 + 5 * p.attr.vit)
}

/** How many goods a serf can shoulder in one trip: 5 per point of Strength
 *  (so a STR 3 weakling hauls 15, a STR 10 ox hauls 50). Missing person = the
 *  weakest roll. */
export const CARRY_PER_STR = 5
export function carryCap(p: Person | undefined): number {
  return (p?.attr.str ?? 3) * CARRY_PER_STR
}

/** Total weight a person is carrying (just food for now; gear/armor weight later). */
export function carriedWeight(p: Person): number {
  return (p.foodItems ?? 0) * FOOD_WEIGHT
}

/** Base move speed (hexes/tick before terrain), from Speed, slowed a touch by load. */
export function personSpeed(p: Person): number {
  const base = 0.28 + 0.024 * p.attr.spd
  return Math.max(0.15, base - carriedWeight(p) * 0.01)
}

/** Effective combat stats for any unit — derived for citizens, table-driven otherwise. */
export interface Combat { atk: number; range: number; period: number; aggro: number; weapon?: WeaponKey }

export function personCombat(p: Person): Combat {
  const w = heldWeapon(p)
  if (w) {
    // Armed: the weapon's reach and damage, sharpened by that weapon's skill.
    const atk = Math.round(w.atk * skillMult(p.skill[w.key] ?? 0) * 10) / 10
    return { atk, range: w.range, period: 1, aggro: GUARD_RANGE, weapon: w.key }
  }
  // No weapon in hand → bare fists (Unarmed skill). Weak, and they'd rather flee.
  const atk = Math.round(WEAPON_COMBAT.unarmed.atk * skillMult(p.skill.unarmed ?? 0) * 10) / 10
  return { atk, range: WEAPON_COMBAT.unarmed.range, period: 1, aggro: 0 }
}

/** A veterancy title from someone's best skill: "Journeyman Logger" etc. */
const TRADE_NOUN: Record<SkillKey, string> = {
  build: 'Builder', farm: 'Farmer', log: 'Logger', mine: 'Miner', haul: 'Porter', fish: 'Fisher',
  craft: 'Smith', unarmed: 'Brawler', spear: 'Spearman', sword: 'Swordsman', bow: 'Archer',
}
export function personTitle(p: Person): string {
  let bestK: SkillKey | null = null
  let bestV = 0
  for (const k of SKILL_KEYS) {
    const v = p.skill[k] ?? 0
    if (v > bestV) { bestV = v; bestK = k }
  }
  if (!bestK || bestV < 2.5) return ''
  const tier = bestV >= 9 ? 'Master' : bestV >= 7 ? 'Expert' : bestV >= 4.5 ? 'Journeyman' : 'Apprentice'
  return `${tier} ${TRADE_NOUN[bestK]}`
}

/** The economy skill a given building's job exercises. */
export function jobSkill(buildingType: string): SkillKey {
  switch (buildingType) {
    case 'farm': return 'farm'
    case 'chop': return 'log'
    case 'plant': return 'log'
    case 'quarry': return 'mine'
    case 'mine': return 'mine'
    case 'smithy': return 'craft'
    case 'workshop': return 'craft'
    case 'barracks': return 'craft'
    case 'workbench': return 'craft'
    case 'pier': return 'fish'
    default: return 'build'
  }
}

/** Grant a citizen experience in one skill and stamp when it was used. */
export function trainSkill(p: Person, key: SkillKey, tick: number, amount = SKILL_XP_PER_TICK): void {
  const v = Math.min(SKILL_MAX, (p.skill[key] ?? 0) + amount)
  p.skill[key] = v
  if (v > (p.peak[key] ?? 0)) p.peak[key] = v
  p.used[key] = tick
}

/** Rust every skill a citizen hasn't used within the grace period. */
export function rustSkills(p: Person, tick: number): void {
  for (const k of SKILL_KEYS) {
    if (tick - (p.used[k] ?? 0) < RUST_GRACE) continue
    const floor = (p.peak[k] ?? 0) * RUST_FLOOR_FRAC
    const v = p.skill[k] ?? 0
    if (v > floor) p.skill[k] = Math.max(floor, v - RUST_PER_TICK)
  }
}

/** Age in whole years for display. */
export function ageYears(p: Person): number {
  return Math.floor(p.age / TICKS_PER_YEAR)
}

/** Is this unit a person (player citizen) with a full skill sheet? */
export function isPerson(u: Unit | { type: string; person?: Person }): boolean {
  return u.type === 'citizen' && !!u.person
}

// ---- Unit-level dispatchers (citizen-aware, else table-driven) ------------------
// These are the single source of truth every server system and the client read,
// so a citizen's stats come from their person while NPCs/wildlife stay table-driven.

export function unitMaxHp(u: Unit): number {
  if (u.type === 'citizen' && u.person) return personMaxHp(u.person)
  return UNITS[u.type].hp
}

export function unitSpeed(u: Unit): number {
  if (u.type === 'citizen' && u.person) return personSpeed(u.person)
  return UNITS[u.type].speed
}

export function unitCombat(u: Unit): Combat {
  if (u.type === 'citizen' && u.person) return personCombat(u.person)
  const d = UNITS[u.type]
  return { atk: d.atk, range: d.range, period: d.period, aggro: d.aggro }
}

/** A non-combatant that flees rather than fights back (anyone not holding a weapon). */
export function isCivilian(u: Unit): boolean {
  if (u.type === 'citizen') return !u.person || !isArmed(u.person)
  return u.type === 'worker' || u.type === 'carrier'
}

/** Is this a player fighter — a citizen holding a weapon? */
export function isSoldierCitizen(u: Unit): boolean {
  return u.type === 'citizen' && !!u.person && isArmed(u.person)
}

// ---- Generation ----------------------------------------------------------------

const FORENAMES = [
  'Bram', 'Edda', 'Cael', 'Rina', 'Tovan', 'Mabel', 'Garrick', 'Ysolde', 'Doran', 'Lysa',
  'Ferro', 'Otta', 'Wystan', 'Bryn', 'Alard', 'Senna', 'Corin', 'Hale', 'Meria', 'Oswin',
  'Perrin', 'Isolde', 'Rowan', 'Talia', 'Gethin', 'Nesta', 'Aldous', 'Fenna', 'Kellan', 'Sable',
  'Aldric', 'Berwyn', 'Cedric', 'Dunstan', 'Eamon', 'Faelan', 'Godric', 'Hadrian', 'Ivor', 'Jorund',
  'Kenric', 'Leofric', 'Merek', 'Nolan', 'Osric', 'Padrig', 'Quillan', 'Roderic', 'Sten', 'Tybalt',
  'Ulric', 'Vance', 'Wilmot', 'Yorath', 'Zane', 'Ansel', 'Baldwin', 'Caradoc', 'Denholm', 'Edwin',
  'Falk', 'Gareth', 'Hamon', 'Ingram', 'Jocelin', 'Kay', 'Lambert', 'Maddoc', 'Norbert', 'Odo',
  'Piers', 'Quentin', 'Ranulf', 'Sayer', 'Terrowin', 'Urien', 'Vortimer', 'Walter', 'Ywain', 'Alwin',
  'Bennet', 'Clement', 'Dryston', 'Eldred', 'Firmin', 'Gawain', 'Harbin', 'Isembard', 'Janshai', 'Kelvin',
  'Lionel', 'Milo', 'Nyle', 'Ordway', 'Percival', 'Quinlan', 'Rademes', 'Severin', 'Tristan', 'Ulf',
  'Vaughn', 'Warrick', 'Yestin', 'Abelard', 'Bertram', 'Colwyn', 'Darian', 'Emeric', 'Fendrel', 'Gyles',
  'Hadwin', 'Innis', 'Jarin', 'Kort', 'Leland', 'Mervyn', 'Neirin', 'Orvyn', 'Payton', 'Ram',
  'Sadon', 'Tomas', 'Uwaine', 'Vachel', 'Willem', 'Yale', 'Arden', 'Brice', 'Carrick', 'Destrian',
  'Erwan', 'Frayne', 'Gerold', 'Halstein', 'Iden', 'Jago', 'Kenway', 'Lucan', 'Madoc', 'Nevan',
  'Osmund', 'Pryce', 'Rhodri', 'Sigurd', 'Tavish', 'Urso', 'Varin', 'Wynstan', 'Yannic', 'Adelmar',
  'Borin', 'Cormac', 'Dathan', 'Eirik', 'Finlo', 'Gorm', 'Hakon', 'Ivar', 'Jarl', 'Kjell',
  'Leif', 'Magnus', 'Njal', 'Orm', 'Ragnar', 'Snorri', 'Torstein', 'Ubbe', 'Vigmar', 'Wulfric',
  'Ynyr', 'Ambrose', 'Benedic', 'Crispin', 'Damaris', 'Ewart', 'Fulke', 'Gervase', 'Hubert', 'Isidore',
  'Jerome', 'Kester', 'Laurence', 'Martius', 'Nicabar', 'Otho', 'Paulin', 'Remy', 'Silas', 'Theobald',
  'Urban', 'Valdis', 'Wendel', 'Anselm', 'Bede', 'Cuthbert', 'Dominic', 'Elias', 'Felix', 'Gregor',
  'Hywel', 'Iestyn', 'Jonas', 'Kaourintin', 'Lazlo', 'Mathis', 'Nudd', 'Owain', 'Pasco', 'Rhun',
  'Sior', 'Tudur', 'Vosper', 'Wyn', 'Aeron', 'Brochan', 'Caswallon', 'Deiniol', 'Emrys', 'Ffransis',
  'Gwilym', 'Heddwyn', 'Iolo', 'Lleu', 'Meurig', 'Onllwyn', 'Pedr', 'Rhisiart', 'Seisyll', 'Taliesin',
  'Adela', 'Berta', 'Cateline', 'Dionisia', 'Elena', 'Felice', 'Gisela', 'Heloise', 'Ida', 'Johanna',
  'Katarin', 'Leda', 'Margery', 'Nell', 'Oriel', 'Petra', 'Quenilda', 'Rosamund', 'Sela', 'Tessa',
  'Una', 'Verena', 'Wilma', 'Ysabel', 'Zaneta', 'Aveline', 'Blanche', 'Clarice', 'Dulcia', 'Edith',
  'Fayette', 'Gwendolyn', 'Hilde', 'Imogen', 'Jenet', 'Kestrel', 'Lettice', 'Maud', 'Nerys', 'Oriana',
  'Pernel', 'Quessa', 'Rohese', 'Sybil', 'Thea', 'Ursula', 'Vivien', 'Wenna', 'Yolande', 'Aoife',
  'Brigid', 'Ciara', 'Deirdre', 'Eithne', 'Fionnuala', 'Grainne', 'Honora', 'Ishbel', 'Keeva', 'Liadan',
  'Maeve', 'Niamh', 'Orla', 'Padraigin', 'Roisin', 'Saoirse', 'Treasa', 'Aelith', 'Brunhild', 'Cerys',
  'Dilys', 'Eluned', 'Ffion', 'Glenys', 'Heulwen', 'Iona', 'Lowri', 'Mairwen', 'Nia', 'Olwen',
  'Rhiannon', 'Sian', 'Tegwen', 'Winifred', 'Angharad', 'Bronwen', 'Carys', 'Delyth', 'Enid', 'Gaenor',
  'Gwenllian', 'Hafwen', 'Idelle', 'Lynet', 'Mererid', 'Nerian', 'Ola', 'Prydwen', 'Rhonwen', 'Seren',
  'Tanwen', 'Ann', 'Beatrix', 'Constance', 'Dorothea', 'Eleanor', 'Frida', 'Gertrude', 'Hannelore', 'Ilse',
  'Jutta', 'Kriemhild', 'Liese', 'Mathilde', 'Nordica', 'Odalys', 'Petronella', 'Reinhild', 'Sigrid', 'Trude',
  'Ulla', 'Vreni', 'Walpurga', 'Astrid', 'Bodil', 'Dagny', 'Embla', 'Freydis', 'Gudrun', 'Helga',
  'Inga', 'Jorunn', 'Kelda', 'Liv', 'Nanna', 'Oddny', 'Ragnhild', 'Solveig', 'Thora', 'Unn',
  'Ylva', 'Asta', 'Birgit', 'Dagmar', 'Estrid', 'Gunnhild', 'Hertha', 'Ingrid', 'Jofrid', 'Karin',
  'Lofn', 'Nessa', 'Ottilie', 'Runa', 'Signy', 'Tove', 'Urd', 'Vigdis', 'Alfhild', 'Amice',
  'Basilia', 'Cecily', 'Denise', 'Emma', 'Florence', 'Golda', 'Hawise', 'Isabel', 'Juliana', 'Lucy',
  'Millicent', 'Nichola', 'Osanna', 'Philippa', 'Quenild', 'Richenda', 'Sabina', 'Theda', 'Utta', 'Wynflaed',
  'Ailith', 'Burga', 'Cwen', 'Eadgyth', 'Frideswide', 'Godgifu', 'Hildith', 'Leofgifu', 'Mildryth', 'Osgyth',
  'Saethryth', 'Wulfrun', 'Aldith', 'Beorhtwyn', 'Eanswith', 'Goldeva', 'Hildred', 'Osburh', 'Sunniva', 'Aelfred',
  'Beorn', 'Caedmon', 'Dunn', 'Eadric', 'Folcard', 'Grim', 'Hengist', 'Ine', 'Leofwin', 'Offa',
  'Penda', 'Saeward', 'Wigstan', 'Aldhelm', 'Botolph', 'Cenwalh', 'Eadgar', 'Frithuwold', 'Godwin', 'Hrothgar',
  'Ludeca', 'Ordgar', 'Sebbi', 'Wulfstan', 'Aescwine', 'Beorhtric', 'Cynric', 'Eorcon', 'Freawine', 'Guthrum',
  'Hlothere', 'Oisc', 'Sigebert', 'Wehha', 'Aethelred', 'Brand', 'Cerdic', 'Ecgbert', 'Fritha', 'Gytha',
  'Hild', 'Osythe', 'Sifled', 'Wynn', 'Aefre', 'Bertha', 'Cyneburga', 'Eormen', 'Frithuswith', 'Godiva',
  'Hereswith', 'Osthryth', 'Sexburga', 'Werburga', 'Aebbe', 'Botilda', 'Cyneswith', 'Eanflaed', 'Hamund', 'Torvald',
  'Eskil', 'Roal', 'Steinar', 'Halvard', 'Geir', 'Auden', 'Brynjar', 'Kolbein', 'Sindri', 'Alvis',
]
const PLACES = [
  'the Vale', 'Ashford', 'Greenmoor', 'the Fen', 'Oakhollow', 'Highcairn', 'Stonebrook',
  'the Marsh', 'Elderwood', 'Coldwater', 'Thornhill', 'the Downs', 'Riverbend', 'Blackmoor',
]

/** A person's first name — the leading word of "Forename of Place". */
export function forenameOf(name: string): string {
  return name.split(' ')[0]
}

/** Build a fresh person with rolled attributes, a skill bias, and maybe an item in hand.
 *  `avoid`: forenames already in use (e.g. by the same town) — the pick skips them,
 *  falling back to the full pool only if all 500 are taken. */
export function makePerson(
  rng: () => number,
  opts: { bias?: Partial<Record<SkillKey, number>>; held?: ItemType; age?: number; avoid?: Set<string> } = {},
): Person {
  const attr = {} as Record<AttrKey, number>
  for (const a of ATTR_KEYS) attr[a] = 3 + Math.floor(rng() * 5) // 3..7
  const skill = {} as Record<SkillKey, number>
  const peak = {} as Record<SkillKey, number>
  const used = {} as Record<SkillKey, number>
  for (const k of SKILL_KEYS) {
    const v = opts.bias?.[k] ?? 0
    skill[k] = v
    peak[k] = v
    used[k] = 0
  }
  const pool = opts.avoid?.size ? FORENAMES.filter((f) => !opts.avoid!.has(f)) : FORENAMES
  const from = pool.length > 0 ? pool : FORENAMES
  const name = `${from[Math.floor(rng() * from.length)]} of ${PLACES[Math.floor(rng() * PLACES.length)]}`
  // Born well fed with a satchel of food.
  return { name, age: opts.age ?? 0, attr, skill, peak, used, held: opts.held, food: FOOD_METER_MAX, foodItems: FOOD_CARRY }
}
